'use client';

import { athenaUrl } from '@/lib/athena-client';
import { isRecord } from '@/lib/athena-utils';

// Posts a GraphQL operation to athena with credentials included. Returns the
// `data` payload on success or `undefined` on transport/graphql errors. Used by
// the header session + leads hooks; intentionally permissive so a transient
// athena failure quietly degrades the header to its unauthenticated state.
export async function athenaPost<T>({
  operationName,
  query,
  variables,
  signal,
}: {
  operationName: string;
  query: string;
  variables: Record<string, unknown>;
  signal?: AbortSignal;
}): Promise<T | undefined> {
  let response: Response;
  try {
    response = await fetch(athenaUrl(), {
      method: 'POST',
      credentials: 'include',
      signal,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'apollographql-client-name': 'beacon',
      },
      body: JSON.stringify({ operationName, query, variables }),
    });
  } catch {
    return undefined;
  }

  if (!response.ok) return undefined;

  const payload: unknown = await response.json().catch(() => undefined);
  if (!isRecord(payload)) return undefined;
  if (Array.isArray(payload['errors']) && payload['errors'].length) return undefined;
  const data = payload['data'];
  if (!isRecord(data)) return undefined;
  return data as T;
}
